import React, { createContext, useContext, useEffect, useState } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '../utils/supabaseClient';

type AuthContextType = {
  session: Session | null;
  user: User | null;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  loading: boolean;
  isAdmin: boolean;
  isActualAdmin: boolean; // True admin status (never changes)
  viewingAsUser: boolean; // Admin is viewing as regular user
  toggleViewAsUser: () => void;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isActualAdmin, setIsActualAdmin] = useState(false);
  const [viewingAsUser, setViewingAsUser] = useState(false);
  
  // isAdmin is false when admin is viewing as user
  const isAdmin = isActualAdmin && !viewingAsUser;
  
  const toggleViewAsUser = () => {
    if (isActualAdmin) {
      setViewingAsUser(prev => !prev);
    }
  };

  // Update last_sign_in_at and award daily login tokens
  const updateLastSignIn = async (userId: string) => {
    try {
      // Get user's last sign in time
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('last_sign_in_at, tokens')
        .eq('id', userId)
        .single();
      
      const now = new Date();
      const lastSignIn = profile?.last_sign_in_at ? new Date(profile.last_sign_in_at) : null;
      
      // Check if it's a new day (more than 20 hours since last sign in)
      const isNewDay = !lastSignIn || (now.getTime() - lastSignIn.getTime()) > 20 * 60 * 60 * 1000;
      
      // Update last sign in time
      await supabase
        .from('user_profiles')
        .update({ last_sign_in_at: now.toISOString() })
        .eq('id', userId);
      
      // Award daily login tokens if it's a new day
      if (isNewDay) {
        const DAILY_LOGIN_TOKENS = 25;
        const newBalance = (profile?.tokens || 0) + DAILY_LOGIN_TOKENS;
        
        // Update balance
        await supabase
          .from('user_profiles')
          .update({ tokens: newBalance })
          .eq('id', userId);
        
        // Record transaction
        await supabase
          .from('token_transactions')
          .insert({
            user_id: userId,
            amount: DAILY_LOGIN_TOKENS,
            type: 'earn',
            reason: 'Daily login bonus',
          });
        
      }
    } catch (error) {
      console.error('Error updating last sign in:', error);
    }
  };

  useEffect(() => {
    let isMounted = true;
    
    // Handle OAuth redirect first
    const handleRedirect = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();

        if (!isMounted) return;

        if (error) {
          console.error("Error getting session:", error);
          setLoading(false);
          return;
        }

        if (data.session) {
          setSession(data.session);
          setUser(data.session.user);
          // Wait for role check with timeout
          await Promise.race([
            checkUserRole(data.session.user.id),
            new Promise(resolve => setTimeout(resolve, 3000)) // 3 second timeout
          ]);
        }
      } catch (error) {
        console.error("Error handling redirect:", error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    handleRedirect();
    
    // Listen for changes on auth state (signed in, signed out, etc.)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!isMounted) return;
      
      setSession(session);
      setUser(session?.user ?? null);
      
      // Check role but don't block - use fire and forget with timeout
      if (session?.user?.id) {
        Promise.race([
          checkUserRole(session.user.id),
          new Promise(resolve => setTimeout(resolve, 3000))
        ]).finally(() => {
          if (isMounted) setLoading(false);
        });
        
        // Update last_sign_in_at when user signs in
        if (event === 'SIGNED_IN') {
          updateLastSignIn(session.user.id);
        }
      } else {
        setIsActualAdmin(false);
        setLoading(false);
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Check if user has admin role
  const checkUserRole = async (userId: string | undefined) => {
    if (!userId) {
      setIsActualAdmin(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('role')
        .eq('id', userId)
        .single();

      if (error) {
        // Only log actual errors, not missing profiles (which is expected for new users)
        if (error.code !== 'PGRST116') {
          console.error('Error fetching user profile:', error);
        }
        setIsActualAdmin(false);
        return;
      }

      const role = data?.role;
      const isAdminRole = role?.toLowerCase() === 'admin';
      setIsActualAdmin(isAdminRole);
    } catch (error) {
      console.error('Error checking user role:', error);
      setIsActualAdmin(false);
    }
  };

  // Sign in with Google
  const signIn = async () => {
    try {
      await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/grow`,
        },
      });
      
      // No need to handle responses here - the redirect will happen automatically
    } catch (error) {
      console.error('Error during sign-in process:', error);
      alert('Failed to sign in with Google. See console for details.');
    }
  };

  // Sign out
  const signOut = async () => {
    try {
      // Clear session storage so welcome modal shows again on next login
      sessionStorage.removeItem('welcome_seen_session');
      
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const value = {
    session,
    user,
    signIn,
    signOut,
    loading,
    isAdmin,
    isActualAdmin,
    viewingAsUser,
    toggleViewAsUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
} 